import React, { useState, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBook,
  faQuestionCircle,
  faSearch,
  faChevronDown,
  faChevronRight,
  faRocket,
  faUserShield,
  faShieldAlt,
  faCalendarAlt,
  faRobot,
  faPlayCircle,
  faChartLine,
  faFileAlt,
  faLock,
  faCog,
  faPlug,
  faUsers,
  faProjectDiagram,
  faCrosshairs,
  faInfoCircle,
  faExternalLinkAlt,
  faListOl,
  faDownload,
  faBuilding,
  faTachometerAlt,
  faChartBar,
  faLink,
  faUser,
  faAtom,
  faRoute
} from '@fortawesome/free-solid-svg-icons';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import PageHeader from '../components/common/PageHeader';
import authService from '../services/authService';

// Section metadata with role visibility
const MANUAL_SECTIONS = [
  { id: 'getting-started', icon: faRocket, iconColor: 'text-purple-500', roles: 'all',
    items: ['welcome', 'first-steps', 'user-roles', 'navigation'] },
  { id: 'projects', icon: faProjectDiagram, iconColor: 'text-purple-500', roles: 'all',
    items: ['creating', 'managing', 'status', 'targets', 'bulk-import'] },
  { id: 'red-team', icon: faCrosshairs, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['logging', 'mitre', 'evidence', 'results'] },
  { id: 'campaigns', icon: faRoute, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'creating', 'auto-detect'] },
  { id: 'attack-chain', icon: faLink, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'kill-chain', 'timeline', 'graph'] },
  { id: 'import-scan', icon: faDownload, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'nessus'] },
  { id: 'agents', icon: faRobot, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'deploying', 'approval', 'tasks'] },
  { id: 'playbooks', icon: faPlayCircle, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['what', 'creating', 'builder', 'executing'] },
  { id: 'atomic', icon: faAtom, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['library', 'running', 'variables', 'syncing'] },
  { id: 'scheduler', icon: faCalendarAlt, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'creating', 'types', 'execution', 'notifications'] },
  { id: 'blue-team', icon: faShieldAlt, iconColor: 'text-blue-500',
    roles: ['admin', 'tenant_admin', 'blue_team', 'purple_team'], roleBadge: 'blueTeam',
    items: ['logging', 'sources', 'correlation'] },
  { id: 'purple-team', icon: faUserShield, iconColor: 'text-purple-500',
    roles: ['admin', 'purple_team'], roleBadge: 'purpleTeam',
    items: ['view', 'gap-analysis', 'heatmap', 'timeline'] },
  { id: 'reports', icon: faFileAlt, iconColor: 'text-purple-500', roles: 'all',
    items: ['types', 'generating', 'navigator', 'project-reports'] },
  { id: 'debrief', icon: faChartLine, iconColor: 'text-purple-500', roles: 'all',
    items: ['overview', 'sections', 'export'] },
  { id: 'security', icon: faLock, iconColor: 'text-green-500', roles: 'all',
    items: ['2fa', 'sessions', 'rate-limiting', 'audit'] },
  { id: 'profile', icon: faUser, iconColor: 'text-purple-500', roles: 'all',
    items: ['info', 'password', 'language'] },
  { id: 'settings', icon: faCog, iconColor: 'text-yellow-500',
    roles: ['admin'], roleBadge: 'admin',
    items: ['branding', 'localization', 'siem', 'notifications', 'remediation'] },
  { id: 'tenant-settings', icon: faBuilding, iconColor: 'text-yellow-500',
    roles: ['tenant_admin'], roleBadge: 'tenantAdmin',
    items: ['overview', 'branding', 'features'] },
  { id: 'user-management', icon: faUsers, iconColor: 'text-yellow-500',
    roles: ['admin', 'tenant_admin'], roleBadge: 'admin',
    items: ['overview', 'creating', 'roles', 'activation'] },
  { id: 'team-management', icon: faUsers, iconColor: 'text-yellow-500',
    roles: ['admin'], roleBadge: 'admin',
    items: ['overview', 'creating', 'members'] },
  { id: 'admin-dashboard', icon: faTachometerAlt, iconColor: 'text-yellow-500',
    roles: ['admin'], roleBadge: 'admin',
    items: ['overview', 'metrics', 'health'] },
  { id: 'executive-dashboard', icon: faChartBar, iconColor: 'text-yellow-500',
    roles: ['admin'], roleBadge: 'admin',
    items: ['overview', 'metrics', 'trends'] },
  { id: 'integrations', icon: faPlug, iconColor: 'text-yellow-500',
    roles: ['admin'], roleBadge: 'admin',
    items: ['siem', 'nessus', 'wazuh'] },
  { id: 'web-tests', icon: faShieldAlt, iconColor: 'text-amber-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'proxy', 'spider', 'scanner', 'repeater', 'intruder', 'websocket', 'auth-profiles', 'authsmash', 'authsmash-automated', 'authsmash-live', 'workflows'] },
  { id: 'c2', icon: faRobot, iconColor: 'text-red-500',
    roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam',
    items: ['overview', 'listeners', 'beacons', 'tasks', 'credentials', 'screenshots', 'file-manager', 'evasion', 'infrastructure', 'redirectors', 'routes', 'metrics'] }
];

// FAQ metadata with role visibility
const FAQ_ITEMS = [
  { id: 'password-reset', roles: 'all' },
  { id: 'feature-visibility', roles: 'all' },
  { id: 'team-members', roles: 'all' },
  { id: 'mitre-version', roles: 'all' },
  { id: 'backups', roles: ['admin'] },
  { id: 'tool-integrations', roles: ['admin'] },
  { id: 'scheduled-assessments', roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'] },
  { id: 'resilience-score', roles: ['admin', 'purple_team'] },
  { id: '2fa-setup', roles: 'all' },
  { id: 'collaboration', roles: 'all' },
  { id: 'report-export', roles: 'all' },
  { id: 'agent-platforms', roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'] },
  { id: 'tenant-setup', roles: ['admin', 'tenant_admin'] },
  { id: 'role-changes', roles: 'all' }
];

// Workflow metadata with role visibility
const WORKFLOW_ITEMS = [
  { id: 'new-project', icon: faProjectDiagram, roles: 'all', steps: 5 },
  { id: 'red-team-assessment', icon: faCrosshairs, roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam', steps: 6 },
  { id: 'blue-team-detection', icon: faShieldAlt, roles: ['admin', 'tenant_admin', 'blue_team', 'purple_team'], roleBadge: 'blueTeam', steps: 5 },
  { id: 'purple-team-exercise', icon: faUserShield, roles: ['admin', 'purple_team'], roleBadge: 'purpleTeam', steps: 6 },
  { id: 'onboarding-users', icon: faUsers, roles: ['admin', 'tenant_admin'], roleBadge: 'admin', steps: 5 },
  { id: 'generating-reports', icon: faFileAlt, roles: 'all', steps: 5 },
  { id: 'web-testing-full', icon: faShieldAlt, roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam', steps: 8 },
  { id: 'c2-operation', icon: faRobot, roles: ['admin', 'tenant_admin', 'red_team', 'purple_team'], roleBadge: 'redTeam', steps: 7 }
];

// Role badge color mapping
const ROLE_BADGE_COLORS = {
  redTeam: 'bg-red-500/20 text-red-400 border-red-500/30',
  blueTeam: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  purpleTeam: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  admin: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  tenantAdmin: 'bg-orange-500/20 text-orange-400 border-orange-500/30'
};

const Help = () => {
  const { darkMode } = useTheme();
  const { t } = useLanguage();
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSections, setExpandedSections] = useState(['getting-started']);
  const [expandedFaq, setExpandedFaq] = useState([]);
  const [expandedWorkflows, setExpandedWorkflows] = useState([]);
  const [activeTab, setActiveTab] = useState('manual');

  // Get current user role
  const currentUser = authService.getCurrentUser();
  const userRole = currentUser?.role || 'red_team';

  // Role-based filter function
  const isVisibleForRole = useCallback((item) => {
    if (item.roles === 'all') return true;
    return Array.isArray(item.roles) && item.roles.includes(userRole);
  }, [userRole]);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const toggleFaq = (faqId) => {
    setExpandedFaq(prev =>
      prev.includes(faqId)
        ? prev.filter(id => id !== faqId)
        : [...prev, faqId]
    );
  };

  const toggleWorkflow = (workflowId) => {
    setExpandedWorkflows(prev =>
      prev.includes(workflowId)
        ? prev.filter(id => id !== workflowId)
        : [...prev, workflowId]
    );
  };

  // Search + role filtering
  const { filteredSections, filteredFaq, filteredWorkflows } = useMemo(() => {
    const search = searchTerm.toLowerCase();

    const matchesSearch = (text) => !search || text.toLowerCase().includes(search);

    // Filter manual sections
    const sections = MANUAL_SECTIONS.filter(section => {
      if (!isVisibleForRole(section)) return false;
      if (!search) return true;
      const title = t(`help.sections.${section.id}.title`);
      if (matchesSearch(title)) return true;
      return section.items.some(itemId => {
        const itemTitle = t(`help.sections.${section.id}.${itemId}.title`);
        const itemText = t(`help.sections.${section.id}.${itemId}.text`);
        return matchesSearch(itemTitle) || matchesSearch(itemText);
      });
    });

    // Filter FAQ
    const faq = FAQ_ITEMS.filter(item => {
      if (!isVisibleForRole(item)) return false;
      if (!search) return true;
      const question = t(`help.faq.${item.id}.question`);
      const answer = t(`help.faq.${item.id}.answer`);
      return matchesSearch(question) || matchesSearch(answer);
    });

    // Filter workflows
    const workflows = WORKFLOW_ITEMS.filter(item => {
      if (!isVisibleForRole(item)) return false;
      if (!search) return true;
      const title = t(`help.workflows.${item.id}.title`);
      const desc = t(`help.workflows.${item.id}.description`);
      if (matchesSearch(title) || matchesSearch(desc)) return true;
      for (let i = 1; i <= item.steps; i++) {
        const stepTitle = t(`help.workflows.${item.id}.step${i}.title`);
        const stepText = t(`help.workflows.${item.id}.step${i}.text`);
        if (matchesSearch(stepTitle) || matchesSearch(stepText)) return true;
      }
      return false;
    });

    return { filteredSections: sections, filteredFaq: faq, filteredWorkflows: workflows };
  }, [searchTerm, userRole, t]);

  const RoleBadge = ({ badgeKey }) => {
    if (!badgeKey) return null;
    return (
      <span className={`text-xs px-2 py-0.5 rounded-full border ${ROLE_BADGE_COLORS[badgeKey] || ''}`}>
        {t(`help.roleBadge.${badgeKey}`)}
      </span>
    );
  };

  const NoResults = () => (
    <div className={`text-center py-12 rounded-lg ${darkMode ? 'bg-gray-800' : 'bg-white border border-gray-200'}`}>
      <FontAwesomeIcon icon={faSearch} className="text-4xl text-gray-400 mb-4" />
      <p className={darkMode ? 'text-gray-400' : 'text-gray-600'}>
        {t('help.page.noResults', { query: searchTerm })}
      </p>
    </div>
  );

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <PageHeader
          title={t('help.page.title')}
          subtitle={t('help.page.subtitle')}
          icon={<FontAwesomeIcon icon={faBook} className="text-purple-500" />}
        />

        {/* Search */}
        <div className="mb-6">
          <div className="relative">
            <FontAwesomeIcon
              icon={faSearch}
              className={`absolute left-3 top-1/2 -translate-y-1/2 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
            />
            <input
              type="text"
              placeholder={t('help.page.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className={`w-full pl-10 pr-4 py-3 rounded-lg border ${
                darkMode
                  ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500'
                  : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
              } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
            />
          </div>
        </div>

        {/* Tabs with match count badges */}
        <div className="flex gap-2 mb-6">
          {[
            { key: 'manual', icon: faBook, count: filteredSections.length },
            { key: 'faq', icon: faQuestionCircle, count: filteredFaq.length },
            { key: 'workflows', icon: faListOl, count: filteredWorkflows.length }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                activeTab === tab.key
                  ? 'bg-purple-600 text-white'
                  : darkMode
                    ? 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <FontAwesomeIcon icon={tab.icon} />
              {t(`help.tabs.${tab.key}`)}
              {searchTerm && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                  activeTab === tab.key
                    ? 'bg-purple-500 text-white'
                    : darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Manual Content */}
        {activeTab === 'manual' && (
          <div className="space-y-3">
            {filteredSections.length === 0 ? <NoResults /> : (
              filteredSections.map(section => (
                <div
                  key={section.id}
                  className={`rounded-lg overflow-hidden ${
                    darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className={`w-full flex items-center justify-between p-4 text-left ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon icon={section.icon} className={`text-lg ${section.iconColor}`} />
                      <span className="font-semibold">
                        {t(`help.sections.${section.id}.title`)}
                      </span>
                      <RoleBadge badgeKey={section.roleBadge} />
                    </div>
                    <FontAwesomeIcon
                      icon={expandedSections.includes(section.id) ? faChevronDown : faChevronRight}
                      className={darkMode ? 'text-gray-500' : 'text-gray-400'}
                    />
                  </button>

                  {expandedSections.includes(section.id) && (
                    <div className={`px-4 pb-4 ${darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100'}`}>
                      {section.items.map((itemId) => (
                        <div key={itemId} className="mt-4">
                          <h4 className="font-medium text-purple-500 mb-1">
                            {t(`help.sections.${section.id}.${itemId}.title`)}
                          </h4>
                          <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                            {t(`help.sections.${section.id}.${itemId}.text`)}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* FAQ Content */}
        {activeTab === 'faq' && (
          <div className="space-y-3">
            {filteredFaq.length === 0 ? <NoResults /> : (
              filteredFaq.map(item => (
                <div
                  key={item.id}
                  className={`rounded-lg overflow-hidden ${
                    darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => toggleFaq(item.id)}
                    className={`w-full flex items-center justify-between p-4 text-left ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon icon={faQuestionCircle} className="text-purple-500" />
                      <span className="font-semibold">
                        {t(`help.faq.${item.id}.question`)}
                      </span>
                    </div>
                    <FontAwesomeIcon
                      icon={expandedFaq.includes(item.id) ? faChevronDown : faChevronRight}
                      className={darkMode ? 'text-gray-500' : 'text-gray-400'}
                    />
                  </button>

                  {expandedFaq.includes(item.id) && (
                    <div className={`px-4 pb-4 ${darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100'}`}>
                      <p className={`mt-3 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                        {t(`help.faq.${item.id}.answer`)}
                      </p>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Workflows Content */}
        {activeTab === 'workflows' && (
          <div className="space-y-3">
            {filteredWorkflows.length === 0 ? <NoResults /> : (
              filteredWorkflows.map(workflow => (
                <div
                  key={workflow.id}
                  className={`rounded-lg overflow-hidden ${
                    darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'
                  }`}
                >
                  <button
                    onClick={() => toggleWorkflow(workflow.id)}
                    className={`w-full flex items-center justify-between p-4 text-left ${
                      darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FontAwesomeIcon icon={workflow.icon} className="text-lg text-purple-500" />
                      <div>
                        <span className="font-semibold">
                          {t(`help.workflows.${workflow.id}.title`)}
                        </span>
                        <p className={`text-sm mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          {t(`help.workflows.${workflow.id}.description`)}
                        </p>
                      </div>
                      <RoleBadge badgeKey={workflow.roleBadge} />
                    </div>
                    <FontAwesomeIcon
                      icon={expandedWorkflows.includes(workflow.id) ? faChevronDown : faChevronRight}
                      className={`flex-shrink-0 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}
                    />
                  </button>

                  {expandedWorkflows.includes(workflow.id) && (
                    <div className={`px-4 pb-4 ${darkMode ? 'border-t border-gray-700' : 'border-t border-gray-100'}`}>
                      {Array.from({ length: workflow.steps }, (_, i) => i + 1).map(stepNum => (
                        <div key={stepNum} className="mt-4 flex gap-4">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                            {stepNum}
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-purple-500 mb-1">
                              {t(`help.workflows.${workflow.id}.step${stepNum}.title`)}
                            </h4>
                            <p className={darkMode ? 'text-gray-300' : 'text-gray-600'}>
                              {t(`help.workflows.${workflow.id}.step${stepNum}.text`)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Quick Links */}
        <div className={`mt-8 p-6 rounded-lg ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`}>
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <FontAwesomeIcon icon={faInfoCircle} className="text-blue-500" />
            {t('help.quickLinks.title')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { key: 'mitreAttack', url: 'https://attack.mitre.org/' },
              { key: 'atomicRedTeam', url: 'https://github.com/redcanaryco/atomic-red-team' },
              { key: 'attackNavigator', url: 'https://mitre-attack.github.io/attack-navigator/' }
            ].map(link => (
              <a
                key={link.key}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex items-center gap-2 p-3 rounded-lg ${
                  darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'
                }`}
              >
                <span>{t(`help.quickLinks.${link.key}`)}</span>
                <FontAwesomeIcon icon={faExternalLinkAlt} className="text-xs text-gray-500" />
              </a>
            ))}
          </div>
        </div>

        {/* Version Info */}
        <div className={`mt-6 text-center text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          <p>{t('help.page.versionInfo')}</p>
          <p>{t('help.page.lastUpdated')}</p>
        </div>
      </div>
    </div>
  );
};

export default Help;
